/**
 * Logging Utilities
 * Centralized logging and audit event utilities
 */

import { logAuditEvent } from '../supabase';
import type { RouteContext } from '../api-route-handlers';

// ============================================================================
// TYPES
// ============================================================================

export interface AuditContext {
  userEmail: string;
  clientIP?: string;
  userAgent?: string;
}

// ============================================================================
// HANDLER LOGGING
// ============================================================================

/**
 * Log handler start with consistent formatting
 */
export function logHandlerStart(
  handlerName: string,
  details?: Record<string, any>
): void {
  console.log(`=== ${handlerName.toUpperCase()} REQUEST ===`);
  if (details) {
    Object.entries(details).forEach(([key, value]) => {
      console.log(`${key}:`, value);
    });
  }
}

/**
 * Log handler error with consistent formatting
 */
export function logHandlerError(
  handlerName: string,
  error: any,
  details?: Record<string, any>
): void {
  console.error(`=== ${handlerName.toUpperCase()} ERROR ===`);
  console.error('Error:', error instanceof Error ? error.message : error);
  if (details) {
    Object.entries(details).forEach(([key, value]) => {
      console.error(`${key}:`, value);
    });
  }
}

/**
 * Log handler completion
 */
export function logHandlerComplete(
  handlerName: string,
  details?: Record<string, any>
): void {
  console.log(`=== ${handlerName.toUpperCase()} COMPLETE ===`);
  if (details) {
    Object.entries(details).forEach(([key, value]) => {
      console.log(`${key}:`, value);
    });
  }
}

// ============================================================================
// AUDIT EVENT LOGGING
// ============================================================================

/**
 * Create audit context from route context
 */
export function createAuditContext(context: RouteContext): AuditContext {
  return {
    userEmail: context.userEmail,
    clientIP: context.clientIP,
    userAgent: context.userAgent,
  };
}

/**
 * Log audit event with consistent pattern
 */
export async function logAuditEventWithContext(
  context: RouteContext,
  action: string,
  category: string,
  details: Record<string, any>
): Promise<void> {
  try {
    await logAuditEvent(
      context.userEmail,
      action,
      category,
      details,
      context.clientIP,
      context.userAgent
    );
  } catch (error) {
    console.error('Error logging audit event:', error);
    // Don't throw - audit logging should not fail the request
  }
}

/**
 * Log successful operation
 */
export async function logSuccessfulOperation(
  context: RouteContext,
  operationType: string,
  operationDetails: Record<string, any>
): Promise<void> {
  await logAuditEventWithContext(context, `${operationType}_success`, 'operation', {
    ...operationDetails,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Log failed operation
 */
export async function logFailedOperation(
  context: RouteContext,
  operationType: string,
  error: Error,
  operationDetails?: Record<string, any>
): Promise<void> {
  await logAuditEventWithContext(context, `${operationType}_failed`, 'operation', {
    ...operationDetails,
    error: error.message,
    timestamp: new Date().toISOString(),
  });
}

// ============================================================================
// SPECIFIC OPERATION LOGGING
// ============================================================================

/**
 * Log open slots calculation
 */
export async function logOpenSlotsCalculation(
  context: RouteContext,
  emailsCount: number,
  dateRange: { start: string; end: string },
  slotDuration: number,
  extendedHours: boolean
): Promise<void> {
  await logAuditEventWithContext(context, 'open_slots_calculation', 'calendar', {
    emailsCount,
    dateRange,
    slotDuration,
    extendedHours,
  });
}

/**
 * Log public booking creation
 */
export async function logPublicBookingCreated(
  context: RouteContext,
  bookerEmail: string,
  bookeeEmail: string,
  eventId: string,
  eventType?: string
): Promise<void> {
  await logAuditEventWithContext(context, 'public_booking_created', 'booking', {
    bookerEmail,
    bookeeEmail,
    eventId,
    eventType,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Log account data cleared
 */
export async function logAccountDataCleared(
  context: RouteContext,
  msftDeleted: boolean,
  googleDeleted: boolean
): Promise<void> {
  await logAuditEventWithContext(context, 'account_data_cleared', 'system', {
    msftDeleted,
    googleDeleted,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Log connection removed
 */
export async function logConnectionRemoved(
  context: RouteContext,
  connectionType: string,
  success: boolean
): Promise<void> {
  await logAuditEventWithContext(context, 'connection_removed', connectionType, {
    connectionType,
    success,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Log connection removal failed
 */
export async function logConnectionRemovalFailed(
  context: RouteContext,
  connectionType: string,
  error: Error
): Promise<void> {
  await logAuditEventWithContext(context, 'connection_removal_failed', connectionType, {
    connectionType,
    error: error.message,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Log external event created
 */
export async function logExternalEventCreated(
  context: RouteContext,
  calendarType: string,
  eventId: string,
  attendeeEmail: string,
  title: string,
  startTime: string,
  endTime: string
): Promise<void> {
  await logAuditEventWithContext(context, 'external_event_created', 'system', {
    calendarType,
    eventId,
    attendeeEmail,
    title,
    startTime,
    endTime,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Log partial availability (some calendars disconnected)
 */
export function logPartialAvailability(
  connectedCount: number,
  disconnectedCount: number,
  connectedEmails: string[],
  disconnectedEmails: string[]
): void {
  console.log(
    `Partial availability: ${connectedCount} connected, ${disconnectedCount} disconnected`
  );
  console.log(`Connected: ${connectedEmails.join(', ')}`);
  console.log(`Disconnected: ${disconnectedEmails.join(', ')}`);
}

/**
 * Log slot filtering results
 */
export function logSlotFiltering(
  originalDayCount: number,
  filteredDayCount: number,
  allowedDurations: number[]
): void {
  console.log(
    `Filtered availability by durations [${allowedDurations.join(', ')}] minutes: ${originalDayCount} days -> ${filteredDayCount} days`
  );
}

/**
 * Log round robin assignment
 */
export function logRoundRobinAssignment(eventType: string, assignedEmail: string): void {
  console.log(`Round robin assignment: ${eventType} -> ${assignedEmail}`);
}

/**
 * Log timezone conversion
 */
export function logTimezoneConversion(
  originalStartUTC: string,
  originalEndUTC: string,
  convertedStartCST: string,
  convertedEndCST: string,
  isOnline: boolean,
  hasConferenceData: boolean
): void {
  console.log('Slot time conversion (UTC -> CST):', {
    originalStartUTC,
    originalEndUTC,
    convertedStartCST,
    convertedEndCST,
    timeZone: 'America/Chicago',
    isOnline,
    hasConferenceData,
  });
}

/**
 * Log booking history storage
 */
export function logBookingHistoryStored(eventType: string): void {
  console.log(`Booking history stored with event type: ${eventType}`);
}

/**
 * Log booking history storage error (non-fatal)
 */
export function logBookingHistoryError(error: any): void {
  console.error('Error storing booking history:', error);
}
